"use client";

import { Box, Group, Stack, UnstyledButton } from "@mantine/core";
import { useRouter } from "next/navigation";
import { FaStar } from "react-icons/fa";
import {
  FiBriefcase,
  FiCheck,
  FiExternalLink,
  FiGift,
  FiInfo,
  FiX,
} from "react-icons/fi";

import {
  formatMaxWorkRewardAmountValue,
  formatMaxWorkRewardCardHeader,
  WORK_REWARD_DIALOG_COLORS,
} from "@/components/model/user/_function/workReward";
import { Button, Modal, Text } from "@/components/ui/elements";

type Props = {
  opened: boolean;
  /** 「内容を確認しました」押下時（勤務確定フロー完了後の既定遷移） */
  onDismiss: () => void;
  /** マイページ・求人一覧など別画面へ遷移する CTA 押下時 */
  onNavigateAway: () => void;
  onMarkSeen: () => void;
};

export function FirstWorkCompleteDialog({
  opened,
  onDismiss,
  onNavigateAway,
  onMarkSeen,
}: Props) {
  const router = useRouter();
  const policyDate = new Date();
  const maxRewardAmount = formatMaxWorkRewardAmountValue(policyDate);
  const cardHeader = formatMaxWorkRewardCardHeader();

  const handleDismiss = () => {
    onMarkSeen();
    onDismiss();
  };

  const handleOpenWorkRewardDetail = () => {
    onMarkSeen();
    onNavigateAway();
    router.push("/mypage?openWorkReward=1");
  };

  const handleBrowseJobs = () => {
    onMarkSeen();
    onNavigateAway();
    router.push("/work");
  };

  return (
    <Modal
      opened={opened}
      close={handleDismiss}
      centered
      size="sm"
      padding={0}
      withCloseButton={false}
      closeOnClickOutside={false}
      styles={{
        content: {
          borderRadius: 16,
          overflow: "hidden",
        },
        body: {
          padding: 0,
        },
      }}
      zIndex={400}
    >
      <Box
        style={{
          backgroundColor: WORK_REWARD_DIALOG_COLORS.headerBg,
          padding: "10px 12px",
        }}
      >
        <Group gap={8} align="center" wrap="nowrap" justify="space-between">
          <Group gap={8} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
            <Box
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: WORK_REWARD_DIALOG_COLORS.headerIcon,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FaStar size={12} color="#fff" aria-hidden />
            </Box>
            <Text
              fz={13}
              fw={700}
              style={{ color: WORK_REWARD_DIALOG_COLORS.headerLabel }}
            >
              今月の勤務特典のお知らせ
            </Text>
          </Group>
          <UnstyledButton
            onClick={handleDismiss}
            aria-label="閉じる"
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              border: "1px solid var(--mantine-color-gray-4)",
              backgroundColor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FiX size={14} color="var(--mantine-color-dark-6)" aria-hidden />
          </UnstyledButton>
        </Group>
      </Box>

      <Stack gap={10} p={12}>
        <Stack gap={4}>
          <Text fz={15} fw={700} lh={1.4} c="dark.8">
            初回勤務おつかれさまでした！
          </Text>
        </Stack>

        <Box
          style={{
            border: `1px solid ${WORK_REWARD_DIALOG_COLORS.activeCellBorder}`,
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <Box
            style={{
              backgroundColor: WORK_REWARD_DIALOG_COLORS.headerIcon,
              padding: "8px 10px",
            }}
          >
            <Group gap={6} align="center" wrap="nowrap">
              <FiGift size={14} color="#fff" style={{ flexShrink: 0 }} aria-hidden />
              <Text fz={12} fw={700} c="white" lh={1.4}>
                {cardHeader}
              </Text>
            </Group>
          </Box>
          <Box
            style={{
              backgroundColor: WORK_REWARD_DIALOG_COLORS.activeCellBg,
              padding: "14px 10px",
            }}
          >
            <Group justify="center" align="baseline" gap={4} wrap="nowrap">
              <Text
                fz={14}
                fw={700}
                style={{ color: WORK_REWARD_DIALOG_COLORS.headerLabel }}
              >
                最大
              </Text>
              <Text
                fz={28}
                fw={700}
                lh={1}
                style={{ color: WORK_REWARD_DIALOG_COLORS.headerIcon }}
              >
                {maxRewardAmount}
              </Text>
              <Text
                fz={14}
                fw={700}
                style={{ color: WORK_REWARD_DIALOG_COLORS.headerLabel }}
              >
                円分
              </Text>
            </Group>
          </Box>
        </Box>

        <Group gap={6} align="flex-start" wrap="nowrap">
          <FiInfo
            size={12}
            color="var(--mantine-color-dark-7)"
            style={{ flexShrink: 0, marginTop: 1 }}
            aria-hidden
          />
          <Text fz={11} c="dark.7" lh={1.45}>
            詳細な条件・金額はマイページの勤務特典からご確認ください
          </Text>
        </Group>

        <UnstyledButton
          onClick={handleOpenWorkRewardDetail}
          style={{ alignSelf: "center" }}
        >
          <Group gap={4} align="center" wrap="nowrap">
            <Text
              fz={12}
              fw={600}
              td="underline"
              style={{ color: WORK_REWARD_DIALOG_COLORS.headerIcon }}
            >
              マイページで勤務特典を確認する
            </Text>
            <FiExternalLink
              size={12}
              color={WORK_REWARD_DIALOG_COLORS.headerIcon}
              aria-hidden
            />
          </Group>
        </UnstyledButton>

        <Stack
          gap={6}
          pt={10}
          style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
        >
          <Button
            fullWidth
            variant="outline"
            color="gray"
            onClick={handleDismiss}
            leftSection={
              <FiCheck
                size={16}
                color={WORK_REWARD_DIALOG_COLORS.headerIcon}
                aria-hidden
              />
            }
            styles={{
              root: {
                height: 40,
                fontSize: 14,
                fontWeight: 600,
                padding: "11px 12px",
                backgroundColor: "#fff",
                color: "var(--mantine-color-dark-7)",
                borderColor: "var(--mantine-color-gray-4)",
              },
            }}
          >
            内容を確認しました
          </Button>
          <Button
            fullWidth
            variant="outline"
            color="gray"
            onClick={handleBrowseJobs}
            leftSection={
              <FiBriefcase
                size={16}
                color="var(--mantine-color-dark-7)"
                aria-hidden
              />
            }
            styles={{
              root: {
                height: 40,
                fontSize: 14,
                fontWeight: 600,
                padding: "11px 12px",
                backgroundColor: "#fff",
                color: "var(--mantine-color-dark-7)",
                borderColor: "var(--mantine-color-gray-4)",
              },
            }}
          >
            次の求人を探す
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}
